import { useParams, useNavigate } from 'react-router-dom'
import { useNonnaProfileViewModel } from '../hooks/useNonnaProfileViewModel'
import { TrustBadge } from '../../../components/shared/TrustBadge'
import { theme } from '../../../core/design-system/theme'

const styles = {
  page: {
    minHeight: '100dvh',
    background: theme.colors.cream,
    paddingBottom: '90px',
  },
  hero: {
    background: `linear-gradient(135deg, ${theme.colors.terracotta}, ${theme.colors.terracottaLight})`,
    padding: '52px 20px 24px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
    position: 'relative',
  },
  backBtn: {
    position: 'absolute',
    top: '52px',
    left: '20px',
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    border: 'none',
    background: 'rgba(255,255,255,0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: '#fff',
  },
  avatar: {
    width: '72px',
    height: '72px',
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.25)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: theme.fonts.display,
    fontSize: '28px',
    fontWeight: 700,
    color: '#fff',
  },
  name: {
    fontFamily: theme.fonts.display,
    fontWeight: 600,
    fontSize: '22px',
    color: '#fff',
    margin: 0,
  },
  stars: {
    display: 'flex',
    gap: '3px',
    alignItems: 'center',
  },
  reviewCount: {
    fontFamily: theme.fonts.body,
    fontSize: '11px',
    color: 'rgba(255,255,255,0.85)',
    marginLeft: '4px',
  },
  body: {
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  statsRow: {
    display: 'flex',
    background: theme.colors.card,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '14px',
    boxShadow: '0 2px 8px rgba(92,61,46,0.05)',
    overflow: 'hidden',
  },
  statCell: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '16px 8px',
    gap: '2px',
    borderRight: `1px solid ${theme.colors.border}`,
  },
  statValue: {
    fontFamily: theme.fonts.body,
    fontWeight: 700,
    fontSize: '17px',
    color: theme.colors.terracotta,
    margin: 0,
  },
  statLabel: {
    fontFamily: theme.fonts.body,
    fontSize: '11px',
    color: theme.colors.textSecondary,
    margin: 0,
  },
  card: {
    background: theme.colors.card,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '14px',
    padding: '14px',
    boxShadow: '0 2px 8px rgba(92,61,46,0.05)',
  },
  cardTitle: {
    fontFamily: theme.fonts.body,
    fontWeight: 700,
    fontSize: '13px',
    color: theme.colors.textPrimary,
    margin: '0 0 8px',
  },
  cardBody: {
    fontFamily: theme.fonts.body,
    fontSize: '13px',
    color: theme.colors.textSecondary,
    lineHeight: '1.6',
    margin: 0,
  },
  badgeRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  ctaBtn: {
    width: '100%',
    fontFamily: theme.fonts.body,
    fontWeight: 700,
    fontSize: '15px',
    color: '#fff',
    background: theme.colors.terracotta,
    border: 'none',
    borderRadius: `${theme.layout.buttonRadius}px`,
    padding: '16px',
    cursor: 'pointer',
    transition: 'opacity 0.15s ease',
  },
}

function StarIcon({ filled }) {
  return (
    <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true">
      <path
        d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
        fill={filled ? theme.colors.honeyGold : 'none'}
        stroke={theme.colors.honeyGold}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function BackChevron() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  )
}

export function NonnaProfileView({ repository }) {
  const { id } = useParams()
  const navigate = useNavigate()
  const { status, nonna, error } = useNonnaProfileViewModel(repository, id)

  if (status === 'idle' || status === 'loading') {
    return (
      <main style={{ ...styles.page, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ fontFamily: theme.fonts.body, color: theme.colors.textSecondary }}>Loading…</p>
      </main>
    )
  }

  if (status === 'failed') {
    return (
      <main style={{ ...styles.page, padding: '40px 20px' }}>
        <button onClick={() => navigate(-1)} style={{ ...styles.backBtn, position: 'static', marginBottom: '16px' }}>
          <BackChevron />
        </button>
        <p style={{ fontFamily: theme.fonts.body, color: theme.colors.textSecondary }}>{error}</p>
      </main>
    )
  }

  const initial = nonna.name?.charAt(0).toUpperCase() ?? '?'
  const filledStars = Math.round(nonna.rating ?? 0)
  const firstName = nonna.name?.split(' ')[0] ?? nonna.name

  return (
    <main style={styles.page} className="page-enter">
      {/* Hero */}
      <section style={styles.hero}>
        <button
          style={styles.backBtn}
          onClick={() => navigate(-1)}
          aria-label="Go back"
          onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.35)')}
          onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.2)')}
        >
          <BackChevron />
        </button>

        <div style={styles.avatar}>{initial}</div>
        <h1 style={styles.name}>{nonna.name}</h1>
        <div style={styles.stars} aria-label={`${filledStars} out of 5 stars`}>
          {Array.from({ length: 5 }, (_, i) => (
            <StarIcon key={i} filled={i < filledStars} />
          ))}
          <span style={styles.reviewCount}>({nonna.reviewCount})</span>
        </div>
      </section>

      {/* Body */}
      <div style={styles.body}>
        {/* Stats */}
        <div style={styles.statsRow}>
          {[
            { value: `${nonna.yearsExperience}`, label: 'Yrs exp.' },
            { value: `$${nonna.hourlyRate}`, label: 'per hour' },
            { value: nonna.rating?.toFixed(1), label: 'rating', noBorder: true },
          ].map(({ value, label, noBorder }) => (
            <div key={label} style={{ ...styles.statCell, ...(noBorder ? { borderRight: 'none' } : {}) }}>
              <p style={styles.statValue}>{value}</p>
              <p style={styles.statLabel}>{label}</p>
            </div>
          ))}
        </div>

        {/* About */}
        {nonna.bio && (
          <div style={styles.card}>
            <p style={styles.cardTitle}>About</p>
            <p style={styles.cardBody}>{nonna.bio}</p>
          </div>
        )}

        {/* Trust badges */}
        {nonna.trustBadges?.length > 0 && (
          <div style={styles.card}>
            <p style={styles.cardTitle}>Verified credentials</p>
            <div style={styles.badgeRow}>
              {nonna.trustBadges.map((badge) => (
                <TrustBadge key={badge} label={badge} withIcon />
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <button
          style={styles.ctaBtn}
          onClick={() => navigate('/book/info', { state: { selectedNonna: nonna } })}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.88')}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
          aria-label={`Book ${nonna.name}`}
        >
          Book {firstName} — ${nonna.hourlyRate}/hr
        </button>
      </div>
    </main>
  )
}

export default NonnaProfileView
